import { useStore } from "ra-core";
import { Link } from "react-router";
import { Check, ChevronDown, ChevronUp, Rocket, X } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Obvious-native getting-started checklist - replaces the upstream installer
 * stepper (and its dashboard-blocking gate). Steps auto-complete from real
 * record counts and tenant config; the last two complete when visited.
 * Collapsible, dismissible, per-user persisted.
 */

interface GettingStartedState {
  dismissed?: boolean;
  collapsed?: boolean;
  visited?: Record<string, boolean>;
}

export const GettingStarted = ({
  totalContacts,
  totalDeals,
  totalNotes,
}: {
  totalContacts: number;
  totalDeals: number;
  totalNotes: number;
}) => {
  const config = useConfigurationContext();
  const [state, setState] = useStore<GettingStartedState>(
    "slate.gettingStarted",
    {},
  );

  if (state.dismissed) return null;

  const steps: {
    key: string;
    label: string;
    why: string;
    to: string;
    done: boolean;
  }[] = [
    {
      key: "contacts",
      label: "Add your contacts",
      why: "Import a CSV from Apollo, ZoomInfo, or any CRM - fields map automatically.",
      to: "/contacts",
      done: totalContacts > 0,
    },
    {
      key: "deal",
      label: "Create a deal",
      why: "The pipeline starts forecasting the moment a deal has a stage and amount.",
      to: "/deals",
      done: totalDeals > 0,
    },
    {
      key: "activity",
      label: "Log an activity",
      why: "Calls, emails, meetings - the timeline is the single source of truth.",
      to: "/contacts",
      done: totalNotes > 0,
    },
    {
      key: "dashboard",
      label: "Install a dashboard",
      why: "One click in the library puts sales and marketing views on this screen.",
      to: "/reports/library",
      done: (config.dashboards ?? []).length > 0,
    },
    {
      key: "form",
      label: "Build a lead-capture form",
      why: "Embed it on your site and every submission lands here with attribution.",
      to: "/marketing",
      done: (config.forms ?? []).length > 0 || !!state.visited?.form,
    },
    {
      key: "scoring",
      label: "Review lead scoring",
      why: "Tune the signals that rank your Hot Contacts and drive workflows.",
      to: "/marketing",
      done: !!state.visited?.scoring,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  const markVisited = (key: string) =>
    setState({
      ...state,
      visited: { ...state.visited, [key]: true },
    });

  return (
    <Card>
      <CardContent className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Rocket className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold flex-1">Getting started</h2>
          <span className="text-xs text-muted-foreground tabular-nums">
            {doneCount}/{steps.length}
          </span>
          <button
            type="button"
            aria-label={state.collapsed ? "Expand" : "Collapse"}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setState({ ...state, collapsed: !state.collapsed })}
          >
            {state.collapsed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </button>
          <button
            type="button"
            aria-label="Dismiss getting started"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setState({ ...state, dismissed: true })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#8fb586] transition-all"
            style={{ width: `${(doneCount / steps.length) * 100}%` }}
          />
        </div>

        {!state.collapsed && (
          <div className="flex flex-col mt-2">
            {steps.map((s) => (
              <div
                key={s.key}
                className="flex items-start gap-2 py-1.5 border-t border-border first:border-t-0"
              >
                <span
                  className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 ${
                    s.done
                      ? "bg-[#8fb586] text-white"
                      : "border border-border text-transparent"
                  }`}
                >
                  <Check className="w-3 h-3" />
                </span>
                <div className="min-w-0 flex-1">
                  {s.done ? (
                    <p className="text-sm text-muted-foreground line-through">
                      {s.label}
                    </p>
                  ) : (
                    <>
                      <Link
                        to={s.to}
                        onClick={() => markVisited(s.key)}
                        className="text-sm font-medium hover:underline"
                      >
                        {s.label}
                      </Link>
                      <p className="text-xs text-muted-foreground">{s.why}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
